// ModernToolRail — Modern Layout の縦アイコンツール帯 (§9.3 / §9.6 全面新規).
//
// 方針 (docs/_wip/modern-layout.md §9.6 / §9.3、consult 2026-06-08):
//   - 相互排他「ツール」のみを置く: ブラシ / 消しゴム / 手のひら(pan) / ズーム。
//     モディファイア (マスク / 筆圧) はここに出さず ModernToolProperties 側 (§9.3)。
//   - 図形 = 後続スライス。スポイト = canvas ジェスチャ (右クリック/Alt+click) で
//     非ボタン (§9.6)。よって今回は出さない。
//   - CSP 流の単一キー割当の目安を title に出す (B/E/Space/Ctrl+wheel)。実キーバインド
//     配線は後続 (シェルは見た目と engine 駆動を確立する)。
//   - アイコンは inline SVG (ModernIcons、新 dep なし)。
//
// engine 駆動: ブラシ/消しゴムは `StrokePen` を pen state に書き戻す (`onSelectPen` /
// `onToggleEraser`)。pan は呼び出し側のトグル、ズームは engine.setZoom (± ボタン)。

import type { CustomCursor, StrokePen } from '@oekaki/protocol';
// M1-a 分数ズーム: fit / ピンチ由来の分数 zoom (例 0.19583…) を「0.2x」に整形する。
import { formatZoomLabel } from '../../../infrastructure/drawing/zoom.js';
import { usePcPointer } from '../../hooks/usePcPointer.js';
import { useTapActivate } from '../../hooks/useTapActivate.js';
import { CustomCursorCanvas } from '../shared/CustomCursorCanvas.js';
import { decodeCursorPixels } from '../shared/customCursorPixels.js';
import {
  BrushIcon,
  EraserIcon,
  EyedropperIcon,
  FlipHorizontalIcon,
  HandIcon,
  RegionIcon,
  RotateIcon,
  ZoomIcon,
} from './ModernIcons.js';
import { formatRotationLabel, highlightToolFor } from './modernTools.js';

export interface ModernToolRailProps {
  /** 現在のペン種 (= どのツールをハイライトするか導く)。 */
  readonly pen: StrokePen;
  /** ブラシツール選択 (= 直前の非消しゴムペンに戻す or brush)。 */
  readonly onSelectBrush: () => void;
  /** 消しゴムツール選択。 */
  readonly onSelectEraser: () => void;
  /**
   * スポイト持続モードが ON か (= ボタンハイライト基準)。§9.6 方針転換 (2026-06-12)。
   * ON のときはブラシ/消しゴムより優先して spuit ボタンだけを点灯させる
   * (`highlightToolFor` が `pen` 由来ハイライトより spuitActive を優先する)。
   */
  readonly spuitActive: boolean;
  /**
   * スポイトツール選択。持続モードに入る (= 選び直すまでスポイトのまま、素クリックで
   * 色拾い)。色拾い自体は protocol 非依存・client ローカル (`spuit.ts`)。
   */
  readonly onSelectSpuit: () => void;
  /**
   * 範囲の道具 (範囲の移動・コピー、docs/_wip/region-move-2026-10-15.md) が選択中か。
   * 点灯時はブラシ / 消しゴム / スポイトを点灯させない (相互排他の持続ツール)。省略時は
   * 範囲ボタンを出さない。
   */
  readonly regionActive?: boolean;
  readonly onSelectRegion?: () => void;
  /** 手のひら (pan) 持続トグルの状態 (= ボタンハイライト基準)。Classic 同様
   *  `panToggleOn` を出す。Space hold の一瞬では点灯させず、明示トグル時のみ点灯
   *  (= 見た目のチラつき回避、consult 2026-06-08)。実 pan は Space hold との OR
   *  (useSpaceHoldPan) が chrome.panActive に流す。 */
  readonly panActive: boolean;
  readonly onTogglePan: () => void;
  /** ズーム ± (engine.setZoom 経由)。 */
  readonly zoom: number;
  readonly zoomMin: number;
  readonly zoomMax: number;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  /**
   * 全体表示 (フィット、決定3 §6 consult 2026-09-29): canvas 全景が表示スロットに
   * 収まる zoom へ 1 手で戻す (`chrome.applyFitZoom`)。ズーム ± クラスタの近くに
   * 置く (desktop Modern 限定、phone シェルにはズーム UI 自体が無いので出さない)。
   */
  readonly onZoomFit: () => void;
  /** 倍率表示のクリックで 100% (1x) に戻す (`chrome.handleZoomReset`、キー `0` と同じ)。 */
  readonly onZoomReset: () => void;
  /**
   * 左右反転 (engine が single source of truth、`chrome.handleToggleFlip`)。zoom と
   * 同じ view 変形系なので zoom 近傍の「view 操作」としてここに置く (2026-06-12、
   * CSP のナビゲーター下部反転クラスタ型を避ける配置)。V キーも同 handler を叩く。
   */
  readonly flipX: boolean;
  readonly onToggleFlip: () => void;
  /**
   * 表示回転 (canvas-rotation 段取り 3)。`rotation` は現在角 (度)、0 以外のときだけ角度
   * 表示 (「23°」) を出し、押すと `onRotateReset` で 0° へ戻す (全端末)。回転ツールの
   * ボタンは PC (`(hover: hover) and (pointer: fine)`) のみ。省略時は回転 UI なし。
   */
  readonly rotation?: number;
  readonly onRotateReset?: () => void;
  readonly rotateToolActive?: boolean;
  readonly onSelectRotateTool?: () => void;
  /**
   * custom-cursor-ux フォローアップ (2026-09-19、作者 staging フィードバック §A-2):
   * ツール帯に「自分のカーソルを描く」補助入口を 1 つ追加 (主導線は参加者リストの自分の
   * 行)。サムネ (確定済みがあれば) または「＋」の点線枠 (未作成) をアイコンとして出す。
   */
  readonly selfCursor: CustomCursor | null;
  /** カーソルエディタが開いているか (= ボタンのハイライト基準)。 */
  readonly cursorEditorOpen: boolean;
  readonly onOpenCursorEditor: () => void;
  /**
   * カーソルボタンに「New!」バッジを出すか (2026-09-19 追補、作者要望「カーソルの
   * 入口ボタンに New! バッジ」)。初回にどの入口からでもカーソルエディタを開くと親
   * (`ModernLayout`) が false に切替える。Classic `CursorHeaderButton` と共有の 1 本の
   * localStorage key なので、片方の入口で消すともう片方でも消える。既定 false。
   */
  readonly showCursorNewBadge?: boolean;
  /** 回転ツールボタン (PC のみ) の「New!」バッジ (2026-10-03)。一度でも回転したら消える。 */
  readonly showRotateNewBadge?: boolean;
  /** 範囲ボタンの「New!」バッジ (2026-10-17)。範囲の道具を一度でも選んだら消える。 */
  readonly showRegionNewBadge?: boolean;
}

/** ツールボタン共通スタイル (CSP 風: 正方アイコン、選択でアクセント)。 */
const toolButtonClass = (active: boolean): string =>
  `flex h-11 w-11 items-center justify-center rounded-md border transition-colors ${
    active
      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black'
      : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white hover:bg-[var(--color-bg)]'
  }`;

/**
 * ツール帯ボタン共通部 — 起動を pointer ベースへ (ペン/Windows Ink の click 不発対策、
 * useTapActivate)。`onActivate` (= 旧 onClick) を pointerup 主経路で叩き、キーボード
 * (Enter/Space) は onClick 温存 + デデュープで通す。挙動 (どのツールに切替わるか) は
 * 呼び出し側の `onActivate` 次第で、本ラッパは起動経路の堅牢化のみを担う。
 */
const ToolRailButton = ({
  ariaLabel,
  ariaPressed,
  title,
  onActivate,
  disabled = false,
  className,
  children,
  manualId,
}: {
  readonly ariaLabel: string;
  readonly ariaPressed?: boolean;
  readonly title: string;
  readonly onActivate: () => void;
  readonly disabled?: boolean;
  readonly className: string;
  readonly children: React.ReactNode;
  /** 使い方マニュアル (user-manual-2026-09-19.md §10.2) の `data-manual` id。 */
  readonly manualId?: string;
}): React.ReactNode => {
  const tap = useTapActivate(disabled ? undefined : onActivate);
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      title={title}
      disabled={disabled}
      data-manual={manualId}
      {...tap}
      className={className}
    >
      {children}
    </button>
  );
};

export const ModernToolRail = ({
  pen,
  onSelectBrush,
  onSelectEraser,
  spuitActive,
  onSelectSpuit,
  regionActive = false,
  onSelectRegion,
  panActive,
  onTogglePan,
  zoom,
  zoomMin,
  zoomMax,
  onZoomIn,
  onZoomOut,
  onZoomFit,
  onZoomReset,
  flipX,
  onToggleFlip,
  rotation = 0,
  onRotateReset,
  rotateToolActive = false,
  onSelectRotateTool,
  selfCursor,
  cursorEditorOpen,
  onOpenCursorEditor,
  showCursorNewBadge = false,
  showRotateNewBadge = false,
  showRegionNewBadge = false,
}: ModernToolRailProps): React.ReactNode => {
  // スポイト持続モードを優先したハイライト導出 (§9.6)。spuit 中はブラシ/消しゴムを
  // 点灯させず spuit だけを点灯する。
  const tool = highlightToolFor(pen, spuitActive, rotateToolActive);
  // 範囲の道具は持続ツール。選ばれている間は他のツールを点灯させない。
  const brushLit = tool === 'brush' && !regionActive;
  const eraserLit = tool === 'eraser' && !regionActive;
  const isPc = usePcPointer();
  const rotationLabel = formatRotationLabel(rotation);
  const selfCursorPixels = selfCursor !== null ? decodeCursorPixels(selfCursor) : null;

  return (
    <div
      role="toolbar"
      aria-orientation="vertical"
      className="flex flex-col items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-1.5"
      aria-label="ツール"
    >
      <ToolRailButton
        ariaPressed={brushLit}
        ariaLabel="ブラシ"
        title="ブラシ (B)"
        onActivate={onSelectBrush}
        className={toolButtonClass(brushLit)}
        manualId="draw.brush"
      >
        <BrushIcon />
      </ToolRailButton>
      <ToolRailButton
        ariaPressed={eraserLit}
        ariaLabel="消しゴム"
        title="消しゴム (E)"
        onActivate={onSelectEraser}
        className={toolButtonClass(eraserLit)}
        manualId="draw.eraser"
      >
        <EraserIcon />
      </ToolRailButton>
      {/* スポイト持続モード (§9.6 方針転換 2026-06-12): ブラシ/消しゴムと同じ相互排他
          ツール。選ぶと選び直すまでスポイトのまま、キャンバスの素クリックで色拾い
          (Alt 不要)。既存の Alt+クリック/右クリック瞬間ジェスチャは別途存続。 */}
      <ToolRailButton
        ariaPressed={spuitActive}
        ariaLabel="スポイト"
        title="スポイト (I)"
        onActivate={onSelectSpuit}
        className={toolButtonClass(tool === 'spuit')}
        manualId="color.eyedropper"
      >
        <EyedropperIcon />
      </ToolRailButton>
      {/* 範囲 (点線の四角): 範囲をドラッグで選び、範囲の近くのボタンで移動 / コピー。
          スポイト・回転と同じ相互排他の持続ツール。 */}
      {onSelectRegion !== undefined ? (
        <ToolRailButton
          ariaPressed={regionActive}
          ariaLabel={showRegionNewBadge ? '範囲 (新機能)' : '範囲'}
          title="範囲を選んで移動・コピー"
          onActivate={onSelectRegion}
          className={`relative ${toolButtonClass(regionActive)}`}
          manualId="draw.region-move"
        >
          <RegionIcon />
          {showRegionNewBadge ? (
            <span
              aria-hidden="true"
              data-testid="region-new-badge"
              className="-top-1.5 -right-1 absolute animate-pulse rounded-full border border-[var(--color-panel)] bg-[var(--color-accent)] px-1 py-px font-bold text-[8px] text-black leading-none shadow"
            >
              New!
            </span>
          ) : null}
        </ToolRailButton>
      ) : null}

      <div className="my-0.5 h-px w-8 bg-[var(--color-border)]" aria-hidden />

      <ToolRailButton
        ariaPressed={panActive}
        ariaLabel="手のひら (移動)"
        title="手のひら / 移動 (Space)"
        onActivate={onTogglePan}
        className={toolButtonClass(panActive)}
        manualId="view.pan"
      >
        <HandIcon />
      </ToolRailButton>

      {/* ズームは ± クラスタ。アイコン下に倍率を出す (CSP の zoom 表示作法)。
          虫眼鏡 = 全体表示 (フィット) ボタン (決定3 §6 consult 2026-09-29、作者 fb で
          別ボタンから虫眼鏡に統合 — 元は押せない飾りだったのでボタンを増やさずに済む)。 */}
      <fieldset className="m-0 flex flex-col items-center gap-1 border-0 p-0" aria-label="ズーム">
        <ToolRailButton
          ariaLabel="全体表示"
          title="全体表示 (F)"
          onActivate={onZoomFit}
          className="flex h-11 w-11 items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white hover:bg-[var(--color-bg)]"
          manualId="view.zoom"
        >
          <ZoomIcon />
        </ToolRailButton>
        <div className="flex gap-1">
          <ToolRailButton
            ariaLabel="縮小"
            title="縮小 (- / ホイール)"
            onActivate={onZoomOut}
            disabled={zoom <= zoomMin}
            className="flex h-5 w-5 items-center justify-center rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] text-[12px] font-bold text-white hover:bg-[var(--color-bg)] disabled:opacity-40"
            manualId="view.zoom"
          >
            −
          </ToolRailButton>
          <ToolRailButton
            ariaLabel="拡大"
            title="拡大 (+ / ホイール)"
            onActivate={onZoomIn}
            disabled={zoom >= zoomMax}
            className="flex h-5 w-5 items-center justify-center rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] text-[12px] font-bold text-white hover:bg-[var(--color-bg)] disabled:opacity-40"
            manualId="view.zoom"
          >
            +
          </ToolRailButton>
        </div>
        {/* 倍率表示 = 100% に戻すボタン (2026-09-29 iPad 実機 fb: 読みやすく + CSP/PS の
            「100%」相当をワンタップで)。虫眼鏡 = 全体表示と対。枠を付けるとレールの縦幅を
            取るので、点線下線だけで「押せる」ことを示す。 */}
        <ToolRailButton
          ariaLabel={`倍率 ${formatZoomLabel(zoom)}、100% に戻す`}
          title="100% に戻す (0)"
          onActivate={onZoomReset}
          className="rounded px-0.5 font-mono text-[11px] leading-tight text-white underline decoration-dotted decoration-[var(--color-text-muted)] underline-offset-2 hover:bg-[var(--color-bg)]"
          manualId="view.zoom"
        >
          {formatZoomLabel(zoom)}
        </ToolRailButton>
      </fieldset>

      {/* 左右反転: zoom と同じ view 変形系として zoom の直下に置く (2026-06-12)。
          CSP のナビ窓下部クラスタ型は踏襲しない。 */}
      <ToolRailButton
        ariaLabel="左右反転"
        ariaPressed={flipX}
        title="キャンバスを左右反転 (V)"
        onActivate={onToggleFlip}
        className={toolButtonClass(flipX)}
        manualId="view.flip"
      >
        <FlipHorizontalIcon />
      </ToolRailButton>

      {/* 回転ツール (PC のみ、= R キーと同じ持続ツール)。タッチは 2 本指ひねりがあるので出さない。 */}
      {isPc && onSelectRotateTool !== undefined ? (
        <ToolRailButton
          ariaLabel={showRotateNewBadge ? '回転 (新機能)' : '回転'}
          ariaPressed={rotateToolActive}
          title="回転 (R)"
          onActivate={onSelectRotateTool}
          className={`relative ${toolButtonClass(tool === 'rotate')}`}
          manualId="view.rotate"
        >
          <RotateIcon />
          {showRotateNewBadge ? (
            <span
              aria-hidden="true"
              className="-top-1.5 -right-1 absolute animate-pulse rounded-full border border-[var(--color-panel)] bg-[var(--color-accent)] px-1 py-px font-bold text-[8px] text-black leading-none shadow"
            >
              New!
            </span>
          ) : null}
        </ToolRailButton>
      ) : null}

      {/* 角度表示 (全端末): 回転中だけ出し、押すと 0° に戻す。出たり消えたりでレールが
          ガタつかないよう、固定高の枠を常に確保して中身だけ出し分ける。 */}
      {onRotateReset !== undefined ? (
        <div className="flex h-4 items-center justify-center">
          {rotationLabel !== null ? (
            <ToolRailButton
              ariaLabel={`回転 ${rotationLabel}、0° に戻す`}
              title="回転をリセット"
              onActivate={onRotateReset}
              className="rounded px-0.5 font-mono text-[11px] leading-tight text-[var(--color-accent)] underline decoration-dotted underline-offset-2 hover:bg-[var(--color-bg)]"
              manualId="view.rotate"
            >
              {rotationLabel}
            </ToolRailButton>
          ) : null}
        </div>
      ) : null}

      <div className="my-0.5 h-px w-8 bg-[var(--color-border)]" aria-hidden />

      {/* custom-cursor-ux フォローアップ (2026-09-19、§A-2): 「自分のカーソルを描く」
          補助入口 (主導線は参加者リストの自分の行)。他ツールと相互排他ではない
          (= モーダル的なエディタを開くだけ) が、見た目の一貫性のためツールボタンと
          同じ `toolButtonClass` / `ToolRailButton` に揃える。 */}
      <ToolRailButton
        ariaPressed={cursorEditorOpen}
        ariaLabel={showCursorNewBadge ? '自分のカーソルを描く (新機能)' : '自分のカーソルを描く'}
        title="自分のカーソルを描く"
        onActivate={onOpenCursorEditor}
        className={`relative ${toolButtonClass(cursorEditorOpen)}`}
        manualId="together.custom-cursor"
      >
        {selfCursorPixels !== null ? (
          <CustomCursorCanvas pixels={selfCursorPixels} sizePx={20} title="自分のカーソル" />
        ) : (
          <span
            aria-hidden="true"
            className="flex h-5 w-5 items-center justify-center rounded-sm border border-dashed border-current text-[11px] leading-none opacity-70"
          >
            ＋
          </span>
        )}
        {/* 「New!」バッジ (2026-09-19 追補、Undo/絵ログ/過去キャンバス入口と同じ視覚
            言語)。意味は aria-label の「(新機能)」が担う (ピルは aria-hidden の装飾)。 */}
        {showCursorNewBadge ? (
          <span
            aria-hidden="true"
            className="-top-1.5 -right-1 absolute animate-pulse rounded-full border border-[var(--color-panel)] bg-[var(--color-accent)] px-1 py-px font-bold text-[8px] text-black leading-none shadow"
          >
            New!
          </span>
        ) : null}
      </ToolRailButton>
    </div>
  );
};
